/**
 * BPTrendChart - Line chart showing BP trends over time
 *
 * Features:
 * - Dual-line chart (systolic and diastolic)
 * - Reference zones for AHA BP categories
 * - Morning/night markers
 * - Statistics display (avg, min, max)
 * - Category distribution
 * - Responsive design
 * - Requires minimum 3 readings
 */

import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea
} from 'recharts';
import { getBPTrends } from '../../services/api';

interface BPTrendChartProps {
  days?: number;
}

const BPTrendChart: React.FC<BPTrendChartProps> = ({ days = 30 }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['bp-trends', days],
    queryFn: () => getBPTrends(days),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  // Transform trend data for Recharts
  const chartData = useMemo(() => {
    if (!data || !data.trend_data) return [];

    return data.trend_data.map((item: any) => {
      const date = new Date(item.date);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      return {
        date: dateStr,
        systolicMorning: item.morning?.systolic || null,
        diastolicMorning: item.morning?.diastolic || null,
        systolicNight: item.night?.systolic || null,
        diastolicNight: item.night?.diastolic || null,
      };
    });
  }, [data]);

  if (isLoading) {
    return (
      <div className="card">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card bg-red-50 border-l-4 border-red-400">
        <p className="text-sm text-red-800">
          Failed to load BP trends. Please try again.
        </p>
      </div>
    );
  }

  if (!data || data.message) {
    return (
      <div className="card text-center py-12">
        <div className="text-6xl mb-4">📈</div>
        <h3 className="text-xl font-serif font-semibold text-neutral-800 mb-2">
          Not Enough Data
        </h3>
        <p className="text-neutral-600">
          {data?.message || 'Record at least 3 BP readings to see trend charts.'}
        </p>
      </div>
    );
  }

  const { statistics, category_distribution } = data;

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card">
            <p className="text-xs text-neutral-500 mb-1">Avg Systolic</p>
            <p className="text-2xl font-bold text-neutral-800">{statistics.avg_systolic}</p>
            <p className="text-xs text-neutral-400">mmHg</p>
          </div>
          <div className="card">
            <p className="text-xs text-neutral-500 mb-1">Avg Diastolic</p>
            <p className="text-2xl font-bold text-neutral-800">{statistics.avg_diastolic}</p>
            <p className="text-xs text-neutral-400">mmHg</p>
          </div>
          <div className="card">
            <p className="text-xs text-neutral-500 mb-1">Avg Pulse</p>
            <p className="text-2xl font-bold text-neutral-800">
              {statistics.avg_pulse || '-'}
            </p>
            <p className="text-xs text-neutral-400">{statistics.avg_pulse ? 'bpm' : ''}</p>
          </div>
          <div className="card">
            <p className="text-xs text-neutral-500 mb-1">Total Readings</p>
            <p className="text-2xl font-bold text-neutral-800">{statistics.total_readings}</p>
            <p className="text-xs text-neutral-400">readings</p>
          </div>
        </div>
      )}

      {/* Trend Chart */}
      <div className="card">
        <h2 className="text-2xl font-serif font-semibold text-neutral-800 mb-6">
          Blood Pressure Trends
        </h2>

        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

            <XAxis
              dataKey="date"
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
              domain={[40, 180]}
              label={{ value: 'mmHg', angle: -90, position: 'insideLeft' }}
            />

            {/* Reference zones for AHA categories */}
            <ReferenceArea y1={40} y2={90} fill="#e0f2fe" fillOpacity={0.3} />
            <ReferenceArea y1={140} y2={180} fill="#fee2e2" fillOpacity={0.3} />

            {/* Reference lines */}
            <ReferenceLine y={120} stroke="#94a3b8" strokeDasharray="3 3" label="Elevated (120)" />
            <ReferenceLine y={130} stroke="#f59e0b" strokeDasharray="3 3" label="Stage 1 (130)" />
            <ReferenceLine y={140} stroke="#ef4444" strokeDasharray="3 3" label="Stage 2 (140)" />

            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px'
              }}
            />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
            />

            <Line
              type="monotone"
              dataKey="systolicMorning"
              stroke="#C98CCC"
              strokeWidth={2}
              name="Systolic (Morning)"
              connectNulls
              dot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="diastolicMorning"
              stroke="#B7A76D"
              strokeWidth={2}
              name="Diastolic (Morning)"
              connectNulls
              dot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="systolicNight"
              stroke="#C98CCC"
              strokeWidth={2}
              strokeDasharray="5 5"
              name="Systolic (Night)"
              connectNulls
              dot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="diastolicNight"
              stroke="#B7A76D"
              strokeWidth={2}
              strokeDasharray="5 5"
              name="Diastolic (Night)"
              connectNulls
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Category Distribution */}
      {category_distribution && (
        <div className="card">
          <h3 className="text-xl font-serif font-semibold text-neutral-800 mb-4">
            Category Distribution
          </h3>
          <div className="space-y-3">
            {Object.entries(category_distribution).map(([category, count]: [string, any]) => {
              const total = statistics?.total_readings || 1;
              const percentage = ((count / total) * 100).toFixed(0);
              const categoryLabels: Record<string, string> = {
                normal: 'Normal',
                elevated: 'Elevated',
                hypertension_stage_1: 'Hypertension Stage 1',
                hypertension_stage_2: 'Hypertension Stage 2',
                hypotensive: 'Hypotensive'
              };

              return (
                <div key={category} className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-neutral-700">{categoryLabels[category]}</span>
                      <span className="text-sm text-neutral-500">{count} ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-neutral-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BPTrendChart;
